import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import {
  Flame, X, Check, Camera, Video, Ghost, ArrowRight, Crown, ShieldCheck, Clock,
} from "lucide-react";

const oldWay = [
  "Photoshopped, years-old profile pictures",
  "Weeks of dry, go-nowhere texting",
  "Dead inboxes full of ghosted matches",
  "Algorithms built to keep you swiping",
];

const sparkdWay = [
  "15-second raw vibe checks — no filters",
  '"Skip the Chat" 5-minute live speed dates',
  "Inactive matches purged automatically",
  "Verified, local, real singles only",
];

const pillars = [
  {
    icon: Camera,
    title: "15s Async Vibe Check",
    desc: "Record straight from your camera. Hear real voices and see real energy before a single text is sent.",
  },
  {
    icon: Video,
    title: "Skip the Chat Live Video",
    desc: "Jump into a 5-minute live speed date. Both feeling it? Extend together, up to 15 minutes.",
  },
  {
    icon: Ghost,
    title: "Anti-Ghosting Inbox Purge",
    desc: "Dead matches are cleared automatically, so your inbox only holds people who actually show up.",
  },
];

export default function Home() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden spark-grid-bg">
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[760px] h-[420px] rounded-full spark-glow-pulse"
          style={{ background: "radial-gradient(circle, rgba(225,29,72,0.18), transparent 70%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-20 pb-14 sm:pt-28 sm:pb-20 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-[#1F242D] text-xs uppercase font-semibold tracking-[0.2em] text-[#F43F5E] spark-fade-up">
            <Flame className="w-3.5 h-3.5" /> Re-inventing Modern Dating
          </span>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white spark-fade-up" style={{ animationDelay: "60ms" }}>
            Dating Apps Are Broken.<br />
            <span className="spark-gradient-text">Sparkd Fixes Them.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed spark-fade-up" style={{ animationDelay: "120ms" }}>
            No endless pen pals. No catfishes. Just authentic 15-second vibe checks and real
            video connections before you ever meet up.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3 spark-fade-up" style={{ animationDelay: "180ms" }}>
            <Link
              to="/join"
              data-testid="hero-cta-founder"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-white spark-gradient spark-gradient-hover transition-all active:scale-95"
            >
              Claim Founder Pass ($99/yr) <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/download"
              data-testid="hero-cta-download"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-slate-200 border border-white/[0.1] bg-[#1F242D] hover:border-[#F43F5E]/40 transition-all"
            >
              Get the App
            </Link>
          </div>
        </div>
      </section>

      {/* The Sparkd Difference */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">The Sparkd Difference</h2>
          <p className="mt-3 text-slate-400">Same goal. Completely different game.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Old way */}
          <div className="rounded-2xl border border-white/[0.06] bg-[#161B22] p-8" data-testid="compare-old">
            <h3 className="font-heading text-lg font-semibold text-slate-400">The Old Way</h3>
            <ul className="mt-5 space-y-4">
              {oldWay.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-500">
                  <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-red-500/10 shrink-0">
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          {/* Sparkd way */}
          <div
            className="relative rounded-2xl border border-[#E11D48]/40 bg-[#1F242D] p-8 overflow-hidden"
            data-testid="compare-sparkd"
            style={{ boxShadow: "0 0 40px rgba(225,29,72,0.12)" }}
          >
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.15), transparent 70%)" }} />
            <h3 className="font-heading text-lg font-semibold spark-gradient-text">The Sparkd Way</h3>
            <ul className="mt-5 space-y-4">
              {sparkdWay.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-slate-200">
                  <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-[#F59E0B]/15 shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature pillars */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid gap-6 md:grid-cols-3">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              className="rounded-2xl spark-card p-7 hover:-translate-y-1 transition-transform duration-300 spark-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`pillar-${i}`}
            >
              <span className="flex items-center justify-center w-12 h-12 rounded-2xl spark-gradient">
                <p.icon className="w-6 h-6 text-white" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founder teaser banner */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="relative rounded-2xl overflow-hidden border border-[#E11D48]/40 bg-[#1F242D] p-8 sm:p-12" data-testid="founder-teaser">
          <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full spark-glow-pulse" style={{ background: "radial-gradient(circle, rgba(225,29,72,0.22), transparent 70%)" }} />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full spark-gradient text-xs font-bold text-white uppercase tracking-wide">
                <Crown className="w-3.5 h-3.5" /> Exclusive Founding Member Status
              </span>
              <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-bold text-white">
                Founder Annual Pass: $99/yr
              </h3>
              <p className="mt-3 text-slate-300">
                All Diamond tier benefits (<span className="text-white font-semibold">$49.99/mo</span> value) plus an exclusive
                Founding Member profile badge. Limited availability per launch corridor.
              </p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-400">
                <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#F59E0B]" /> $99/yr</span>
                <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#F59E0B]" /> Limited availability</span>
              </div>
            </div>
            <Link
              to="/join"
              data-testid="founder-teaser-cta"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-white spark-gradient spark-gradient-hover transition-all active:scale-95 shrink-0"
            >
              Claim Founder Pass <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
